import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";

const inputCls = "w-full bg-background border border-border px-3 py-3 outline-none focus:ring-2 focus:ring-primary text-sm";

export default function ResetPassword() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Remove the token from the address bar and browser history once read.
  useEffect(() => {
    if (window.location.search) window.history.replaceState(null, "", "/reset-password");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: form.password });
      setUser(null);
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-5 py-16">
        <div className="border-2 border-white/20 bg-card p-8 brutal-shadow-sm">
          <h1 className="font-display text-4xl mb-1">NEW PASSWORD</h1>
          {!token ? (
            <p data-testid="reset-missing-token" className="text-sm text-muted-foreground">This reset link is invalid or has expired. Please request a new one.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4 mt-6">
              <input required type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="New password (8+ characters)" data-testid="reset-password" className={inputCls} />
              <input required type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                placeholder="Confirm new password" data-testid="reset-password-confirm" className={inputCls} />
              {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
              <button type="submit" disabled={busy} data-testid="reset-submit"
                className="w-full bg-primary text-primary-foreground font-mono-caps text-xs py-3.5 border border-primary hover-lift disabled:opacity-50">
                {busy ? "..." : "Set New Password"}
              </button>
            </form>
          )}
          <p className="mt-6 text-sm text-muted-foreground">
            <Link to="/forgot-password" data-testid="reset-request-new-link" className="text-primary hover:underline font-medium">Request a new link</Link>
            {" · "}
            <Link to="/login" data-testid="reset-back-to-login" className="text-primary hover:underline font-medium">Back to sign in</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
